/**
 * Shared language entry formatting utilities.
 * Legacy: pro-frontend/src/app/utils/language-display.util.ts
 */

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const CEFR_ORDER: Record<string, number> = {
  A1: 1,
  A2: 2,
  B1: 3,
  B2: 4,
  C1: 5,
  C2: 6,
};

const CEFR_LABELS: Record<string, string> = {
  A1: 'Beginner',
  A2: 'Basic',
  B1: 'Intermediate',
  B2: 'Conversational',
  C1: 'Advanced',
  C2: 'Fluent',
};

type LanguageLevels = {
  listening_level?: string | null;
  reading_level?: string | null;
  spoken_interaction_level?: string | null;
  spoken_production_level?: string | null;
  writing_level?: string | null;
  proficiency_level?: string | null;
  normal_level?: string | null;
  title?: string | null;
};

function normalizeCefrLevel(level: string | null | undefined): string | null {
  if (!level || typeof level !== 'string') {
    return null;
  }
  const trimmed = level.trim().toUpperCase();
  return CEFR_LEVELS.indexOf(trimmed) !== -1 ? trimmed : null;
}

export function deriveMaxCefrFromLevels(levels: LanguageLevels): string | null {
  const arr = [
    levels.listening_level,
    levels.reading_level,
    levels.spoken_interaction_level,
    levels.spoken_production_level,
    levels.writing_level,
  ].filter((l) => !!l && typeof l === 'string') as string[];

  if (arr.length === 0) {
    return null;
  }

  const normalized = arr
    .map((l) => normalizeCefrLevel(l))
    .filter((l): l is string => !!l);

  if (normalized.length === 0) {
    return null;
  }

  return normalized.reduce((max, curr) => {
    const maxVal = CEFR_ORDER[max] || 0;
    const currVal = CEFR_ORDER[curr] || 0;
    return currVal > maxVal ? curr : max;
  });
}

export function formatLanguageEntryForDisplay(
  entry: LanguageLevels | null | undefined,
  mode: string,
): { title: string; description: string } {
  const name = entry && entry.title ? entry.title : '';

  if (mode === 'name_only') {
    return { title: name, description: '' };
  }

  if (mode === 'name_normal') {
    const level = ((entry && entry.normal_level) || '').trim();
    return { title: level ? `${name} \u2013 ${level}` : name, description: '' };
  }

  if (mode === 'name_cefr') {
    const level =
      ((entry && entry.proficiency_level) || '').trim() ||
      deriveMaxCefrFromLevels({
        listening_level: entry && entry.listening_level,
        reading_level: entry && entry.reading_level,
        spoken_interaction_level: entry && entry.spoken_interaction_level,
        spoken_production_level: entry && entry.spoken_production_level,
        writing_level: entry && entry.writing_level,
      });
    const label = level ? CEFR_LABELS[level] : undefined;
    const suffix = level ? (label ? `${level} - ${label}` : level) : '';
    return { title: suffix ? `${name} (${suffix})` : name, description: '' };
  }

  if (mode === 'grid') {
    const skills = [
      { label: 'Listening', val: entry && entry.listening_level },
      { label: 'Reading', val: entry && entry.reading_level },
      { label: 'Spoken interaction', val: entry && entry.spoken_interaction_level },
      { label: 'Spoken production', val: entry && entry.spoken_production_level },
      { label: 'Writing', val: entry && entry.writing_level },
    ];
    const filled = skills.filter((s) => s.val && String(s.val).trim());
    if (filled.length === 0) {
      return { title: name, description: '' };
    }
    const levels = filled.map((s) => s.val);
    const allSame = levels.every((l) => l === levels[0]);
    if (filled.length === 5 && allSame) {
      return { title: name, description: `All skills: ${levels[0]}` };
    }
    return {
      title: name,
      description: filled.map((s) => `${s.label} ${s.val}`).join(' · '),
    };
  }

  return { title: name, description: '' };
}
